import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
} from "@material-ui/core";
import { X } from "lucide-react";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import UpgradeCoin from "../../assets/images/UpgradeCoin.png";
import creditsIcon from "../../assets/images/creditsIcon.svg";
import upgradeIcon from "../../assets/images/upgradeIcon.svg";
import CoinIcon from "../../assets/svgs/CoinIcon";
import { trackOfferViewed } from "../../analytics/canonicalTracking";
import { useAppSelector } from "../../redux/typedHooks";

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    backgroundColor: "#131313",
    color: "white",
    borderRadius: "12px",
  },
  dialogTitle: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(3),
    paddingRight: "68px",
    paddingBottom: 0,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      paddingBottom: 0,
    },
  },
  mainTextContaner: {
    display: "flex",
    flexDirection: "column",
    gap: 32,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column-reverse",
      gap: 24
    },
  },
  mainText: {
    fontSize: "32px",
    fontWeight: 700,
    lineHeight: "120%",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
      marginTop: "0",
    },
  },
  creditsInfoContainer: {
    gap: 24,
    marginTop: 32,
    marginBottom: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: 10,
      color: "#FFFFFFCC",
      fontSize: 16
    },
  },
  textInfo: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontWeight: 700,
    [theme.breakpoints.down("sm")]: {
      justifyContent: "space-between",
      width: "100%",
    }
  },
  divider: {
    backgroundColor: "#1F1F1F",
    width: 2,
    height: 35,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: 1,
    },
  },
  dialogContent: {
    padding: theme.spacing(5),
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: "8px 16px 32px",
    },
  },
  dialogActions: {
    padding: theme.spacing(3),
    borderTop: "1px solid #333",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  closeButton: {
    color: "#fff",
    minWidth: "auto",
    padding: "4px",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
  alertIcon: {
    color: "#ffc107",
    marginRight: theme.spacing(1),
  },
  title: {
    color: "white",
    fontWeight: 600,
    fontSize: "20px",
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
    },
  },
  description: {
    color: "#ccc",
    fontSize: "16px",
    lineHeight: 1.5,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
  statusBox: {
    backgroundColor: "#0f0f0f",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statusTitle: {
    color: "#999",
    marginBottom: theme.spacing(1),
    fontSize: "14px",
  },
  statusItem: {
    color: "#fff",
    marginBottom: theme.spacing(0.5),
    fontSize: "14px",
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(4),
    justifyContent: "center",
    marginTop: 20,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: 12
    },
  },
  upgradeButton: {
    backgroundColor: "#FE5A97",
    color: "white",
    flex: 1,
    padding: "17px 24px",
    borderRadius: "6px",
    textTransform: "none",
    fontSize: "20px",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#e04a85",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      padding: "6px 24px",
    },
  },
  creditsButton: {
    borderColor: "#FE5A97",
    color: "#fff",
    flex: 1,
    padding: "17px 24px",
    borderRadius: "6px",
    textTransform: "none",
    fontSize: "20px",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "rgba(254, 90, 151, 0.1)",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      padding: "6px 24px",
    },
  },
  closeButtonText: {
    color: "#999",
    textTransform: "none",
    fontSize: "14px",
  },
}));

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  creditsRemaining: number;
  freeMessagesRemaining: number;
  freeMessagesLimit: number;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  creditsRemaining,
  freeMessagesRemaining,
  freeMessagesLimit,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const user = useAppSelector((state: any) => state.user.currentUser);
  const offerTracked = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      offerTracked.current = false;
      return;
    }
    if (offerTracked.current) return;
    offerTracked.current = true;
    const role = user?.userType?.title?.toLowerCase?.() || "guest";
    trackOfferViewed({
      offer_type: "subscription",
      user_id: user?.id,
      user_role:
        role === "viewer" || role === "creator" || role === "model"
          ? role
          : "guest",
    });
  }, [isOpen, user?.id, user?.userType?.title]);

  const handleUpgrade = () => {
    onClose();
    history.push("/subscriptions");
  };

  const handleBuyCredits = () => {
    onClose();
    history.push("/subscriptions");
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
        style: {
          width: isMobile ? "calc(100% - 32px)" : undefined,
          maxWidth: 640,
          margin: isMobile ? "16px" : undefined,
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography className={classes.title} component="span">

        </Typography>
        <Button onClick={onClose} className={classes.closeButton}>
          <X size={32} strokeWidth={1} />
        </Button>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.mainTextContaner}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img src={UpgradeCoin} alt="altCoinIcon" />
          </div>
          <Box className={classes.mainText}>
            You need more credits <br /> to send a message 
          </Box>
        </div>
        <div className={classes.creditsInfoContainer}>
          <div className={classes.textInfo}>
            <p
              style={{
                margin: 0,
                padding: 0,
              }}
            >
              Credits required:
            </p>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 2
            }}>
              <CoinIcon /><span>1</span>
            </div>
          </div>
          <Divider className={classes.divider} />
          <div className={classes.textInfo} >
            <p
              style={{
                margin: 0,
                padding: 0,
              }}
            >
              Your balance:
            </p>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 2
            }}>
              <CoinIcon />
              {creditsRemaining && creditsRemaining}
            </div>
          </div>
        </div>

        <Box className={classes.buttonsContainer}>
          <Button
            variant="outlined"
            startIcon={
              <img
                src={creditsIcon}
                alt="Credits"
                style={{ width: 20, height: 20 }}
              />
            }
            onClick={handleBuyCredits}
            className={classes.creditsButton}
          >
            Buy more credits
          </Button>

          <Button
            variant="contained"
            startIcon={
              <img
                src={upgradeIcon}
                alt="Upgrade plan"
                style={{ width: 20, height: 20 }}
              />
            }
            onClick={handleUpgrade}
            className={classes.upgradeButton}
          >
            Upgrade plan
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
